import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trash2, Lock, Clock, AlertTriangle, Wallet, Receipt, Printer, FileDown } from 'lucide-react';
import { differenceInHours } from 'date-fns';
import DataManager from '../components/DataManager';
import CurrencyDisplay, { CurrencyLabel } from '../components/CurrencyDisplay';
import PrintReportModal from '../components/PrintReportModal';
import { DirhamIconColored } from '../components/DirhamIcon';
import { safeFormat, safeParseDate } from '../utils/dateUtils';

// فترة السماح للتعديل للحلاق (ساعة واحدة)
const EDIT_WINDOW_HOURS = 1;

const Expenses = () => {
  const { t } = useTranslation();
  const { isAdmin, isBarber, user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [advance, setAdvance] = useState({ amount: 0 });
  const [advanceAlerts, setAdvanceAlerts] = useState([]);
  const [formData, setFormData] = useState({
    amount: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    receipt_number: ''
  });
  const [advanceFormData, setAdvanceFormData] = useState({ amount: '', description: '' });
  const [showPrintModal, setShowPrintModal] = useState(false);

  useEffect(() => {
    fetchExpenses();
    fetchAdvance();
    fetchAdvanceAlerts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchExpenses = async () => {
    try {
      const res = await api.get('/expenses');
      setExpenses(res.data);
    } catch (error) {
      toast.error('فشل تحميل المصروفات');
    } finally {
      setLoading(false);
    }
  };

  // Download PDF Report (Admin only)
  const downloadPDFReport = async () => {
    try {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const startDate = firstDayOfMonth.toISOString().split('T')[0];
      const endDate = today.toISOString().split('T')[0];
      
      const response = await api.get(`/reports/pdf/expenses?start_date=${startDate}&end_date=${endDate}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expenses_report_${startDate}_to_${endDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('تم تحميل التقرير بنجاح');
    } catch (error) {
      toast.error('فشل تحميل التقرير');
    }
  };

  const fetchAdvance = async () => {
    try {
      const res = await api.get('/advance');
      setAdvance(res.data);
    } catch (error) {
      console.error('Failed to fetch advance');
    }
  };

  const fetchAdvanceAlerts = async () => {
    try {
      const res = await api.get('/advance/alerts');
      setAdvanceAlerts(res.data.alerts || []);
    } catch (error) {
      console.error('Failed to fetch advance alerts');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        date: new Date(formData.date).toISOString(),
        added_by: user?.id,
        added_by_name: user?.name
      };
      const res = await api.post('/expenses', payload);
      
      // عرض رسالة النجاح مع معلومات الخصم من السلفة
            toast.success(<>تم إضافة المصروف وخصم {payload.amount} <DirhamIconColored className="w-3 h-3 inline" /> من السلفة</>);
      
      // عرض تنبيهات السلفة إذا وجدت
      if (res.data.alerts && res.data.alerts.length > 0) {
        res.data.alerts.forEach(alert => {
          if (alert.type === 'critical') {
            toast.error(alert.message);
          } else {
            toast.warning(alert.message);
          }
        });
      }
      
      setOpen(false);
      setFormData({ amount: '', category: '', date: new Date().toISOString().split('T')[0], description: '', receipt_number: '' });
      fetchExpenses();
      fetchAdvance(); // تحديث رصيد السلفة
      fetchAdvanceAlerts(); // تحديث التنبيهات
    } catch (error) {
      toast.error('فشلت العملية');
    }
  };

  const handleAdvanceSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await api.put('/advance', {
        amount: parseFloat(advanceFormData.amount),
        description: advanceFormData.description || 'السلفة'
      });
      toast.success('تم تحديث السلفة بنجاح');
      setAdvanceOpen(false);
      setAdvanceFormData({ amount: '', description: '' });
      fetchAdvance();
      fetchAdvanceAlerts();
      
      // Show alerts if any
      if (res.data.alerts && res.data.alerts.length > 0) {
        res.data.alerts.forEach(alert => {
          if (alert.type === 'critical') {
            toast.error(alert.message);
          } else {
            toast.warning(alert.message);
          }
        });
      }
    } catch (error) {
      toast.error('فشل تحديث السلفة');
    }
  };

  // التحقق إذا كان المصروف قابل للتعديل للحلاق (ساعة واحدة)
  const canBarberEditExpense = (expense) => {
    if (!isBarber()) return false;
    if (expense.added_by !== user?.id) return false;
    const createdAt = new Date(expense.created_at);
    const minutesElapsed = (new Date() - createdAt) / (1000 * 60);
    return minutesElapsed < 60; // ساعة واحدة = 60 دقيقة
  };

  // حساب الوقت المتبقي للتعديل (بالدقائق)
  const getTimeRemaining = (expense) => {
    const createdAt = new Date(expense.created_at);
    const minutesElapsed = (new Date() - createdAt) / (1000 * 60);
    const minutesRemaining = 60 - minutesElapsed;
    if (minutesRemaining <= 0) return null;
    if (minutesRemaining < 1) return 'أقل من دقيقة';
    return `${Math.floor(minutesRemaining)} دقيقة متبقية`;
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المصروف؟ سيتم إرجاع المبلغ إلى السلفة.')) return;
    try {
      const res = await api.delete(`/expenses/${expenseId}`);
      toast.success(res.data.message || 'تم حذف المصروف وإرجاع المبلغ للسلفة');
      fetchExpenses();
      fetchAdvance(); // تحديث رصيد السلفة
      fetchAdvanceAlerts(); // تحديث التنبيهات
    } catch (error) {
      toast.error('فشل حذف المصروف');
    }
  };

  // المصروفات للمدير والحلاقين فقط
  if (!isAdmin() && !isBarber()) {
    return <div className="text-center py-12"><p className="text-xl text-muted-foreground">ليس لديك صلاحية للوصول إلى هذه الصفحة</p></div>;
  }

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div></div>;
  }

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
  const canAdd = isAdmin() || isBarber();

  return (
    <div className="space-y-6">
      {/* Advance Alerts */}
      {advanceAlerts.length > 0 && advanceAlerts.map((alert) => (
        <div 
          key={alert.barber_id || alert.message}
          className={`p-4 rounded-xl border flex items-center gap-3 ${
            alert.type === 'critical' 
              ? 'bg-red-500/10 border-red-500/30 text-red-500' 
              : 'bg-amber-500/10 border-amber-500/30 text-amber-500'
          }`}
        >
          <AlertTriangle className="w-6 h-6" />
          <span className="font-semibold">{alert.message}</span>
        </div>
      ))}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('expenses')}</h1>
          <p className="text-muted-foreground">إدارة المصروفات والسلفة</p>
        </div>
        <div className="flex gap-3">
          <DataManager
            collectionName="expenses"
            collectionLabel="المصروفات"
            items={expenses}
            onRefresh={fetchExpenses}
            idField="id"
            nameField="description"
            isAdmin={isAdmin()}
          />
          
          {/* زر طباعة التقرير */}
          <Button 
            onClick={() => setShowPrintModal(true)} 
            variant="outline" 
            className="border-primary/50 text-primary hover:bg-primary/10"
          >
            <Printer className="w-4 h-4 me-2" />
            طباعة تقرير
          </Button>
          
          {/* زر تحميل PDF (للمدير فقط) */}
          {isAdmin() && (
            <Button 
              onClick={downloadPDFReport} 
              variant="outline" 
              className="border-green-500/50 text-green-500 hover:bg-green-500/10"
            >
              <FileDown className="w-4 h-4 me-2" />
              تحميل PDF
            </Button>
          )}
          
          {canAdd && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-expense-btn" className="bg-primary text-primary-foreground">
                  <Plus className="w-4 h-4 me-2" />{t('addExpense')}
                </Button>
              </DialogTrigger>
              
            <DialogContent className="bg-card border-white/10">
              <DialogHeader><DialogTitle>{t('addExpense')}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2"><CurrencyLabel label={t('amount')} /></label>
                  <Input data-testid="expense-amount-input" type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required className="bg-secondary/50 border-white/10" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('category')}</label>
                  <Input data-testid="expense-category-input" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} required className="bg-secondary/50 border-white/10" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('date')}</label>
                  <Input data-testid="expense-date-input" type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required className="bg-secondary/50 border-white/10" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <Receipt className="w-4 h-4" />
                    رقم الإيصال / الفاتورة
                  </label>
                  <Input 
                    data-testid="expense-receipt-input" 
                    value={formData.receipt_number} 
                    onChange={(e) => setFormData({ ...formData, receipt_number: e.target.value })} 
                    placeholder="اختياري - مثال: INV-001"
                    className="bg-secondary/50 border-white/10" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('description')}</label>
                  <Input data-testid="expense-description-input" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-secondary/50 border-white/10" />
                </div>
                {isBarber() && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-500" />
                    <span className="text-sm text-amber-500">يمكنك التعديل خلال ساعة واحدة فقط من وقت الإضافة. الحذف متاح للمدير فقط.</span>
                  </div>
                )}
                <Button data-testid="submit-expense-btn" type="submit" className="w-full bg-primary text-primary-foreground">{t('save')}</Button>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Total Expenses */}
        <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
          <p className="text-sm text-muted-foreground mb-2">{t('totalExpenses')}</p>
          <p className="text-3xl font-bold text-red-500"><CurrencyDisplay amount={totalExpenses} className="text-red-500" /></p>
        </div>

        {/* Advance (السلفة) */}
        <div className={`bg-card border rounded-xl shadow-2xl p-6 ${
          advance.amount <= 100 ? 'border-red-500/50 bg-red-500/5' : 
          advance.amount <= 250 ? 'border-amber-500/50 bg-amber-500/5' : 'border-white/5'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                <Wallet className="w-4 h-4" />
                السلفة
              </p>
              <p className={`text-3xl font-bold ${
                advance.amount <= 100 ? 'text-red-500' : 
                advance.amount <= 250 ? 'text-amber-500' : 'text-green-500'
              }`}>
                <CurrencyDisplay amount={advance.amount || 0} />
              </p>
              {advance.amount <= 100 && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  مستوى حرج - يجب إضافة مبلغ!
                </p>
              )}
              {advance.amount > 100 && advance.amount <= 250 && isAdmin() && (
                <p className="text-xs text-amber-500 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  تنبيه - السلفة منخفضة
                </p>
              )}
            </div>
            {isAdmin() && (
              <Dialog open={advanceOpen} onOpenChange={setAdvanceOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="border-primary/50">
                    تعديل السلفة
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-white/10">
                  <DialogHeader><DialogTitle>تعديل السلفة</DialogTitle></DialogHeader>
                  <form onSubmit={handleAdvanceSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2"><CurrencyLabel label="المبلغ الحالي" /></label>
                      <Input 
                        type="number" 
                        step="0.01" 
                        value={advanceFormData.amount} 
                        onChange={(e) => setAdvanceFormData({ ...advanceFormData, amount: e.target.value })} 
                        placeholder={`الحالي: ${advance.amount || 0}`}
                        required 
                        className="bg-secondary/50 border-white/10" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">ملاحظة</label>
                      <Input 
                        value={advanceFormData.description} 
                        onChange={(e) => setAdvanceFormData({ ...advanceFormData, description: e.target.value })} 
                        placeholder="اختياري"
                        className="bg-secondary/50 border-white/10" 
                      />
                    </div>
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-sm">
                      <p className="font-semibold text-amber-500 mb-2">تنبيهات السلفة:</p>
                      <ul className="text-muted-foreground space-y-1">
                                                <li>• عند 250 <DirhamIconColored className="w-3 h-3 inline" /> أو أقل: تنبيه للمدير فقط</li>
                        <li>• عند 100 <DirhamIconColored className="w-3 h-3 inline" /> أو أقل: تنبيه للمدير والحلاقين</li>
                      </ul>
                    </div>
                    <Button type="submit" className="w-full bg-primary text-primary-foreground">حفظ</Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>

      {/* Expenses Table */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary/50 border-b border-white/5">
              <tr>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('date')}</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('category')}</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('amount')}</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">رقم الإيصال</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('description')}</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">أضافه</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((expense) => {
                const canBarberEdit = canBarberEditExpense(expense);
                const timeRemaining = isBarber() && expense.added_by === user?.id ? getTimeRemaining(expense) : null;
                
                return (
                  <tr key={expense.id} className="border-b border-white/5 hover:bg-secondary/20">
                    <td className="px-6 py-4">{safeFormat(expense.date, 'yyyy-MM-dd', { fallback: '-' })}</td>
                    <td className="px-6 py-4">{expense.category}</td>
                    <td className="px-6 py-4 text-red-500 font-semibold"><CurrencyDisplay amount={expense.amount} className="text-red-500" /></td>
                    <td className="px-6 py-4">
                      {expense.receipt_number ? (
                        <span className="bg-primary/10 text-primary px-2 py-1 rounded text-sm flex items-center gap-1 w-fit">
                          <Receipt className="w-3 h-3" />
                          {expense.receipt_number}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">{expense.description || '-'}</td>
                    <td className="px-6 py-4 text-sm">{expense.added_by_name || 'المدير'}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {/* زر الحذف - للمدير فقط */}
                        {isAdmin() && (
                          <Button data-testid={`delete-expense-${expense.id}`} onClick={() => handleDelete(expense.id)} variant="ghost" size="sm" className="text-red-500 hover:bg-red-500/10">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* للحلاق - إظهار الوقت المتبقي للتعديل أو رسالة انتهاء الفترة */}
                        {isBarber() && expense.added_by === user?.id && (
                          timeRemaining ? (
                            <span className="text-xs text-amber-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {timeRemaining}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Lock className="w-3 h-3" />
                              انتهت فترة التعديل
                            </span>
                          )
                        )}
                        
                        {/* للحلاق الآخر - لا يظهر شيء */}
                        {isBarber() && expense.added_by !== user?.id && (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* نافذة طباعة التقرير */}
      <PrintReportModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        reportType="expenses"
        isAdmin={isAdmin()}
      />
    </div>
  );
};

export default Expenses;
